/* Copyright (c) 2025 MOCA Systems, Inc. All rights reserved. */

import * as appStrings from '@touchplan/strings';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from 'src/app/common/material/material-module';
import { MatDialogModule } from '@angular/material/dialog';
import { PresentationModule } from 'src/app/common/presentation-components/presentation.module';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DialogReusableModule } from 'src/app/common/dialog-reusable/dialog-reusable.module';
import { SharedUIMaterialModule } from 'src/app/shared/ui/material/shared-ui-material.module';
import { BehaviorSubject, combineLatest, of, Subject } from 'rxjs';
import { map, switchMap, take, startWith, shareReplay, takeUntil, filter } from 'rxjs/operators';
import { ImportTicketsStateService } from '../../services/import-tickets-state.service';
import { IFormalTicket, IProcessingItem, TicketCorrectionService } from './ticket-correction.service';
import ticketValidator from '@touchplan/config/lib/ticket-validators.json';
import { SecureImage } from 'src/app/common/secure-image/secure-image.component';

enum SelectedTabIndex {
	CONFLICTED,
	RESOLVED,
}

@Component({
	selector: 'import-tickets-ticket-correction',
	standalone: true,
	imports: [
		CommonModule,
		MaterialModule,
		MatDialogModule,
		PresentationModule,
		ReactiveFormsModule,
		DialogReusableModule,
		SharedUIMaterialModule,
		SecureImage,
	],
	providers: [TicketCorrectionService],
	templateUrl: './ticket-correction.component.html',
	styleUrls: ['./ticket-correction.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TicketCorrectionComponent implements OnInit, OnDestroy {
	private formBuilder = inject(FormBuilder);
	private importTicketsStateService = inject(ImportTicketsStateService);
	private ticketCorrectionService = inject(TicketCorrectionService);

	private shutdownSubject$ = new Subject<void>();
	public appStrings = appStrings;
	// for the <input> tags
	protected inputValidationConfig = {
		crewMin: ticketValidator.liveCrewSize.min,
		crewMax: ticketValidator.liveCrewSize.max,
		// existing edit does this to avoid the steppers stepping by 0.01 <_<
		durationMin: Math.floor(ticketValidator.liveDurationDays.min),
		durationMinForValidationText: ticketValidator.liveDurationDays.min,
		durationMax: ticketValidator.liveDurationDays.max,
	};

	public LOADING_FAILED_IMAGE = 'assets/images/image-failed.png';
	public DEFAULT_IMAGE_SKELETON = 'assets/images/skeleton.png';

	public selectedTabIndex$ = new BehaviorSubject<SelectedTabIndex>(SelectedTabIndex.CONFLICTED);

	protected locationList$ = this.importTicketsStateService.locationList;
	protected roleList$ = this.importTicketsStateService.roleList;

	public conflictArray = this.formBuilder.array<FormGroup<IProcessingItem>>([]);
	public resolvedArray = this.formBuilder.array<FormGroup<IProcessingItem>>([]);

	public formalTicket$ = this.ticketCorrectionService.loadProcessingTickets();
	/** single emission observable that will "wait" until everything's loaded but fire immediately after loading */
	public waitForInitialization$ = this.formalTicket$.pipe(
		map(() => true),
		startWith(false),
		filter((initialized) => initialized), // ensure we only proceed once the tickets are loaded
		take(1),
		shareReplay(1),
		takeUntil(this.shutdownSubject$)
	);

	public embiggenedItem$ = new BehaviorSubject<IFormalTicket | null>(null);

	// these are split up once automatically, then manually by the user afterwards
	public conflictedTickets$ = new BehaviorSubject<IFormalTicket[]>([]);
	public resolvedTickets$ = new BehaviorSubject<IFormalTicket[]>([]);
	public excludedTickets$ = new BehaviorSubject<IFormalTicket[]>([]);

	// ...so picky
	public submitButtonText$ = combineLatest([
		this.selectedTabIndex$,
		this.conflictedTickets$,
		this.resolvedTickets$,
	]).pipe(
		map(([selectedTabIndex, conflictedTickets, resolvedTickets]) => {
			if (selectedTabIndex === SelectedTabIndex.CONFLICTED && conflictedTickets.length > 0) {
				return appStrings.IMPORT_TICKET_SKIP.toUpperCase();
			} else if (selectedTabIndex === SelectedTabIndex.RESOLVED) {
				return appStrings.IMPORT_TICKET_PLACE_TICKETS(resolvedTickets.length).toUpperCase();
			}
			return appStrings.UICONTROL_TEXT_NEXT.toUpperCase();
		})
	);

	// there's no [formArray] directive so it _has to_ be wrapped in a formGroup to use in the template <_<
	public formGroup = this.formBuilder.group({
		conflictArray: this.conflictArray,
		resolvedArray: this.resolvedArray,
	});

	private setupFormArray() {
		this.formalTicket$
			.pipe(
				map((ticketsWithFormGroups) => {
					return this.ticketCorrectionService.splitProcessingTickets(ticketsWithFormGroups);
				})
			)
			.subscribe((ticketsWithFormGroups) => {
				this.ticketCorrectionService.reloadFormArray(
					ticketsWithFormGroups.conflictedTickets,
					this.conflictArray
				);
				this.ticketCorrectionService.reloadFormArray(
					ticketsWithFormGroups.resolvedTickets,
					this.resolvedArray
				);

				this.conflictedTickets$.next(ticketsWithFormGroups.conflictedTickets);
				this.resolvedTickets$.next(ticketsWithFormGroups.resolvedTickets);
			});
	}

	public excludeTickets(
		tickets: IFormalTicket[],
		sourceList: BehaviorSubject<IFormalTicket[]>,
		sourceFormArray: FormArray
	): void {
		this.ticketCorrectionService.moveTickets(tickets, sourceList, this.excludedTickets$, sourceFormArray);
		this.moveToResolvedOnEmpty();
	}

	/** ui only, directly corresponded to clicking the resolve button on a single row */
	public resolveTicketFromConflictUi(ticket: IFormalTicket): void {
		if (ticket.formGroup.invalid) {
			ticket.formGroup.markAllAsTouched();
			// some attempts to force the mat-extended-select to show the error state
			// suspect it's not respecting the ngControl's state and just considering the
			// state of it's internal control, thus making this not possible
			// leaving behind for... posterity?
			// ticket.formGroup.get('roleId')?.markAsTouched();
			// ticket.formGroup.get('roleId')?.markAsDirty();
			return;
		}

		this.ticketCorrectionService.moveTickets(
			[ticket],
			this.conflictedTickets$,
			this.resolvedTickets$,
			this.conflictArray,
			this.resolvedArray
		);
		this.moveToResolvedOnEmpty();
	}

	private moveToResolvedOnEmpty() {
		if (
			this.selectedTabIndex$.getValue() === SelectedTabIndex.CONFLICTED &&
			this.conflictedTickets$.getValue().length === 0
		) {
			this.selectedTabIndex$.next(SelectedTabIndex.RESOLVED);
		}
	}

	public onNext(): void {
		const onConflictedTab = this.selectedTabIndex$.getValue() === SelectedTabIndex.CONFLICTED;

		// first tab skips
		if (onConflictedTab) {
			this.skipAllConflictingTickets();
			return;
		}

		if (this.conflictArray.invalid || this.resolvedArray.invalid) {
			this.conflictArray.markAllAsTouched();

			if (this.conflictArray.invalid && !onConflictedTab) {
				this.selectedTabIndex$.next(SelectedTabIndex.CONFLICTED);
			}
			return;
		}
		this.saveTicketsFromForm();
	}

	public skipAllConflictingTickets() {
		this.excludeTickets(this.conflictedTickets$.getValue(), this.conflictedTickets$, this.conflictArray);
		this.selectedTabIndex$.next(SelectedTabIndex.RESOLVED);
	}

	public toggleImageBigness(item: IFormalTicket): void {
		if (item === this.embiggenedItem$.getValue()) {
			this.embiggenedItem$.next(null);
		} else {
			this.embiggenedItem$.next(item);
		}
	}

	private saveTicketsFromForm() {
		combineLatest([
			this.resolvedTickets$,
			this.excludedTickets$,
			this.importTicketsStateService.projectId$,
			this.importTicketsStateService.uploadId$,
		])
			.pipe(
				take(1),
				switchMap(([resolvedTickets, excludedTickets, projectId, jobId]) => {
					// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7046
					// - swap for mocks
					// return this.ticketCorrectionService.saveTicketsFromForm(
					// 	resolvedTickets,
					// 	excludedTickets,
					// 	'-M7s3MqPjHV-0Isg_k-c',
					// 	'steve'
					// );
					return this.ticketCorrectionService.saveTicketsFromForm(
						resolvedTickets,
						excludedTickets,
						projectId,
						jobId
					);
					// END PLACEHOLDER REPLACE AS PART OF TP-7046
				})
			)
			.subscribe((result) => {
				// componentStore did not like the non-observable or empty version
				this.importTicketsStateService.nextStep(of({}));
			});
	}

	// ngFor-ism
	public trackByTicketId(index: number, item: IFormalTicket): string {
		return item.ticket.ticketId;
	}

	public ngOnInit(): void {
		this.setupFormArray();
	}

	public ngOnDestroy(): void {
		this.embiggenedItem$.complete();
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
	}
}
